/**
 * Экран согласия на передачу данных бренду.
 *
 * Показывает не «бренд запрашивает ваши данные», а сами значения: «Душанбе»,
 * «14.03.1992». Разница принципиальная — человек соглашается на то, что видит,
 * а не на формулировку. Чего не хватает, он тут же и заполняет.
 *
 * Отказ ничего не стоит: код не потрачен, сканировать можно снова.
 */
import type { ConsentRequest } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Field, Icon, Sheet } from '@allswipes/ui';
import { useEffect, useState } from 'react';

import { api } from '../api';

import { useT } from '@allswipes/i18n';

export function ConsentSheet({
  campaignId,
  onDone,
  onClose,
}: {
  campaignId: string;
  /** Вызывается после согласия: экран скана повторяет попытку сам. */
  onDone: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [ask, setAsk] = useState<ConsentRequest | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .consent(campaignId)
      .then((data) => {
        if (!alive) return;
        setAsk(data);
        setValues(data.values);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof ApiError ? err.message : t('Не удалось открыть'));
      });
    return () => {
      alive = false;
    };
  }, [campaignId]);

  const submit = async () => {
    if (!ask) return;
    setBusy(true);
    setErrors({});
    setError(null);
    try {
      await api.giveConsent(campaignId, values);
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields ?? {});
        setError(err.message);
      } else {
        setError(t('Не получилось сохранить'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('Бренд запрашивает данные')} onClose={onClose}>
      {!ask ? (
        <p className="sub">{error ?? t('Загружаем…')}</p>
      ) : (
        <>
          <p className="sub" style={{ marginBottom: 14 }}>
            {t('{brand} просит эти данные для участия в акции «{campaign}». Перечень полей есть и в правилах акции — пункт 7.', {
          brand: ask.brand?.name ?? t('Бренд'),
          campaign: ask.campaign.name,
        })}
      </p>

          <div className="stack">
            {ask.fields.map((field) => {
              const value = values[field.key] ?? '';
              const known = (ask.values[field.key] ?? '') !== '';
              return (
                <div key={field.key} className="consent-row">
                  <div className="row" style={{ marginBottom: 6 }}>
                    <span className="h2">{field.label}</span>
                    <span className="spacer" />
                    {known ? (
                      <Badge tone="good">{t('уже заполнено')}</Badge>
                    ) : (
                      <Badge tone="warn">{t('нужно заполнить')}</Badge>
                    )}
                  </div>
                  <div className="sub" style={{ marginBottom: 8 }}>
                    {t('Зачем: {why}', { why: field.why })}
                  </div>

                  {field.kind === 'select' ? (
                    <Field label="" error={errors[field.key]}>
                      <select
                        className="select"
                        value={value}
                        onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                      >
                        <option value="">{t('Выбери вариант')}</option>
                        {(field.options ?? []).map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <Field label="" hint={field.hint} error={errors[field.key]}>
                      <input
                        className={`input ${field.kind === 'date' ? 'mono' : ''}`}
                        value={value}
                        placeholder={field.kind === 'date' ? '14.03.1992' : ''}
                        inputMode={field.kind === 'email' ? 'email' : 'text'}
                        onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                      />
                    </Field>
                  )}
                </div>
              );
            })}
          </div>

          <Card style={{ marginTop: 4 }}>
            <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
              <Icon name="shield" size={16} />
              <span className="sub">
                {t('Передаётся только то, что в этом списке. Остальная анкета остаётся при тебе, а запись о передаче появится в профиле — «Кому ты передал данные». Отказ ничего не стоит: код останется неиспользованным.')}
              </span>
            </div>
          </Card>

          {error && (
            <p className="sub" style={{ color: 'var(--bad)', marginTop: 10 }}>
              {error}
            </p>
          )}

          <div className="row" style={{ gap: 8, marginTop: 14 }}>
            <Button variant="pri" block disabled={busy} onClick={submit}>
              {busy ? t('Сохраняем…') : t('Передать и участвовать')}
            </Button>
            <Button variant="out" onClick={onClose}>
              {t('Не сейчас')}
            </Button>
          </div>
        </>
      )}
    </Sheet>
  );
}
